import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react"; // Optional: or use any icon
import { FaUpload, FaSpinner, FaEye } from "react-icons/fa";
import API from "../../config/config";

const UploadProfilePic = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [existingProfileImage, setExistingProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFullImage, setShowFullImage] = useState(false);
  const navigate = useNavigate();

  // Fetch existing profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("userPublicToken");
        const response = await fetch(API.ADMIN.USERDASHBOARD, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.profileImage) {
            setExistingProfileImage(data.data.profileImage);
          }
        }
      } catch (error) {
        console.error("Failed to fetch profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setFile(selected);
    setMessage("");

    if (selected) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(selected);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file first.");
      return;
    }

    setUploading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("userPublicToken");
      const formdata = new FormData();
      formdata.append("type", "profilepic");
      formdata.append("documentFor", "Driver");
      formdata.append("file", file);

      const response = await fetch(API.ADMIN.DOCUMENT_UPLOAD, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formdata,
      });

      const result = await response.json();
      if (response.ok) {
        setMessage("✅ Profile picture uploaded successfully.");
        setExistingProfileImage(result.document?.documentUrl); // Update existing image
        setPreviewUrl(null); // Clear preview
        setFile(null); // Clear file
        console.log(result);
        // Optional: navigate back after a short delay
        setTimeout(() => navigate("/user-dashboard"), 1000);
      } else {
        setMessage(result.message || "❌ Upload failed.");
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ An error occurred while uploading.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white border border-gray-300 rounded-xl shadow-xl p-8 mt-10">
      {/* Header with back arrow */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate("/user-dashboard")}
          className="mr-3 text-gray-700 hover:text-indigo-600"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">
          {existingProfileImage
            ? "Update Profile Picture"
            : "Upload Profile Picture"}
        </h2>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      ) : (
        <>
          {/* Current/Existing Profile Picture */}
          {existingProfileImage && !previewUrl && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-700 mb-4">
                Profile Picture:
              </h3>
              <div className="flex flex-col items-center">
                <img
                  src={existingProfileImage}
                  alt="Profile Picture"
                  className="w-72 h-72 object-cover rounded-lg border shadow"
                />
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={() => setShowFullImage(true)}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <FaEye />
                    View Full Image
                  </button>
                  <label className="cursor-pointer flex gap-2 items-center text-sm text-blue-600 hover:text-blue-800">
                    <FaUpload />
                    Change
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Image preview for new file */}
          {previewUrl && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-700 mb-4">
                New Profile Picture Preview:
              </h3>
              <div className="flex flex-col items-center">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-72 h-72 object-cover rounded-lg border shadow"
                />
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      uploading
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    {uploading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setPreviewUrl(null);
                      setFile(null);
                      setMessage("");
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upload section - only show if no existing image */}
          {!existingProfileImage && !previewUrl && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-700 mb-4">
                Upload Profile Picture:
              </h3>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="mb-4 w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
          )}

          {message && (
            <div className="text-center">
              <p className="text-sm text-gray-700">{message}</p>
            </div>
          )}

          {/* Full Image Modal */}
          {showFullImage && existingProfileImage && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
              <div className="relative max-w-4xl max-h-full p-4">
                <img
                  src={existingProfileImage}
                  alt="Profile Picture Full View"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
                <button
                  onClick={() => setShowFullImage(false)}
                  className="absolute top-2 right-2 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100"
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UploadProfilePic;
